/**
 * Onglet Contrats - ÉTAPE F
 * Upload, liste, catégorisation, téléchargement sécurisé, suppression
 * Gestion des avenants (amendments)
 */

import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import { useAuth } from "../../../App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import { Progress } from "../../ui/progress";
import { ScrollArea } from "../../ui/scroll-area";
import { Textarea } from "../../ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../ui/alert-dialog";
import { 
  Upload, FileText, File, Plus, Eye, Download, Trash2, 
  X, Loader2, AlertCircle, FolderOpen, FileSignature, 
  Calendar, Link2, AlertTriangle
} from "lucide-react";
import { DatePickerFr, formatDateFr } from "../../ui/date-picker-fr";
import { 
  CONTRACT_DOC_TYPE_OPTIONS,
  getContractDocTypeLabel,
  getContractDocTypeColor
} from "../../../lib/hr-constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Max file size: 10 MB
const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Allowed file types for contracts
const ALLOWED_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
];

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes) return "-";
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} Mo`;
};

// File type display
const getFileIcon = (mimeType) => {
  if (mimeType?.includes("pdf")) return { icon: FileText, color: "text-red-500" };
  if (mimeType?.includes("word")) return { icon: FileText, color: "text-blue-500" };
  return { icon: File, color: "text-slate-500" };
};

// Validate file
const validateFile = (file) => {
  if (file.size > MAX_FILE_SIZE) {
    return { valid: false, error: "Le fichier dépasse la taille maximale de 10 Mo" };
  }
  if (!ALLOWED_TYPES.includes(file.type) && !file.name.endsWith('.pdf') && !file.name.endsWith('.doc') && !file.name.endsWith('.docx')) {
    return { valid: false, error: "Type de fichier non autorisé. Formats acceptés : PDF, DOC, DOCX" };
  }
  return { valid: true };
};

// Contract list item
const ContractItem = ({ contract, onView, onDownload, onDelete, onAddAmendment, allContracts }) => {
  const { icon: FileIcon, color: iconColor } = getFileIcon(contract.mime_type);
  const typeColor = getContractDocTypeColor(contract.type);
  
  // Find amendments for this contract (only if this is NOT an amendment itself)
  const isAmendment = contract.type === "amendment";
  const amendments = !isAmendment ? (allContracts?.filter(c => 
    c.parent_contract_id === contract.id && !c.is_deleted && c.type === "amendment"
  ) || []) : [];
  
  return (
    <div className={`rounded-lg border transition-colors ${
      isAmendment ? 'border-purple-200 bg-purple-50/30 ml-6' : 'border-slate-200 hover:bg-slate-50'
    }`}>
      <div className="flex items-start gap-3 p-4 group">
        {/* File icon */}
        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 mt-0.5">
          <FileIcon className={`w-5 h-5 ${iconColor}`} />
        </div>
        
        {/* Contract info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <p className="text-sm font-medium text-slate-900">
              {contract.subject}
            </p>
            <Badge variant="outline" className={`${typeColor} text-xs shrink-0`}>
              {getContractDocTypeLabel(contract.type)}
              {isAmendment && contract.amendment_number && ` #${contract.amendment_number}`}
            </Badge>
            {contract.is_current && (
              <Badge className="bg-green-100 text-green-700 text-xs">
                En vigueur
              </Badge>
            )}
          </div>
          
          <div className="flex items-center gap-4 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              Signé le {formatDateFr(contract.signature_date)}
            </span>
            <span>
              Effet au {formatDateFr(contract.effective_date)}
            </span>
            {contract.end_date && (
              <span className="text-orange-600">
                Fin le {formatDateFr(contract.end_date)}
              </span>
            )}
          </div>
          
          <p className="text-xs text-slate-400 mt-1">
            {contract.original_filename} • {formatFileSize(contract.file_size)}
          </p>
        </div>
        
        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => onView(contract)}
            title="Visualiser"
            data-testid={`view-contract-${contract.id}`}
          >
            <Eye className="w-4 h-4" />
          </Button>
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8"
            onClick={() => onDownload(contract)}
            title="Télécharger"
            data-testid={`download-contract-${contract.id}`}
          >
            <Download className="w-4 h-4" />
          </Button>
          {!isAmendment && (
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-8 w-8"
              onClick={() => onAddAmendment(contract)}
              title="Ajouter un avenant"
              data-testid={`amend-contract-${contract.id}`}
            >
              <Link2 className="w-4 h-4" />
            </Button>
          )}
          <Button 
            variant="ghost" 
            size="icon" 
            className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => onDelete(contract)}
            title="Supprimer"
            data-testid={`delete-contract-${contract.id}`}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>
      
      {/* Amendments nested under this contract - simple list, no recursion */}
      {amendments.length > 0 && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-2 space-y-2">
          <p className="text-xs font-medium text-slate-500 px-2">
            {amendments.length} avenant(s)
          </p>
          {amendments
            .sort((a, b) => (b.amendment_number || 0) - (a.amendment_number || 0))
            .map(amendment => (
              <AmendmentItem
                key={amendment.id}
                amendment={amendment}
                onView={onView}
                onDownload={onDownload}
                onDelete={onDelete}
              />
            ))}
        </div>
      )}
    </div>
  );
};

// Simple amendment item (no nesting to avoid recursion)
const AmendmentItem = ({ amendment, onView, onDownload, onDelete }) => {
  const { icon: FileIcon, color: iconColor } = getFileIcon(amendment.mime_type);
  const typeColor = getContractDocTypeColor(amendment.type);
  
  return (
    <div className="flex items-center gap-3 p-3 rounded-lg border border-purple-200 bg-purple-50/30 ml-4 group">
      <div className="w-8 h-8 rounded-lg bg-white flex items-center justify-center shrink-0">
        <FileIcon className={`w-4 h-4 ${iconColor}`} />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-slate-900 truncate">
            {amendment.subject}
          </p>
          <Badge variant="outline" className={`${typeColor} text-xs shrink-0`}>
            Avenant #{amendment.amendment_number}
          </Badge>
        </div>
        <p className="text-xs text-slate-500">
          Effet au {formatDateFr(amendment.effective_date)}
        </p>
      </div>
      
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-7 w-7"
          onClick={() => onView(amendment)}
        >
          <Eye className="w-3.5 h-3.5" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-7 w-7"
          onClick={() => onDownload(amendment)}
        >
          <Download className="w-3.5 h-3.5" />
        </Button>
        <Button 
          variant="ghost" 
          size="icon" 
          className="h-7 w-7 text-red-500 hover:text-red-600 hover:bg-red-50"
          onClick={() => onDelete(amendment)}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
};

// Contract viewer modal
const ContractViewer = ({ contract, open, onClose, token }) => {
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    if (!open || !contract) return;
    
    const fetchContract = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(
          `${API}/hr/files/${contract.file_id}/download`,
          {
            headers: { Authorization: `Bearer ${token}` },
            responseType: 'blob'
          }
        );
        const url = URL.createObjectURL(response.data);
        setBlobUrl(url);
      } catch (err) {
        console.error("Error fetching contract:", err);
        setError("Impossible de charger le contrat");
      } finally {
        setLoading(false);
      }
    };
    
    fetchContract();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contract, token]);
  
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);
  
  const handleDownload = () => {
    if (blobUrl && contract) {
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = contract.original_filename;
      link.click();
    }
  };
  
  const isPdf = contract?.mime_type === "application/pdf";
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-8">
            <span className="truncate">{contract?.subject}</span>
            <Button variant="outline" size="sm" onClick={handleDownload}>
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
          </DialogTitle>
          <DialogDescription>
            {contract?.original_filename}
          </DialogDescription>
        </DialogHeader>
        
        <div className="mt-4 overflow-auto" style={{ maxHeight: "70vh" }}>
          {loading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          )}
          
          {error && (
            <div className="flex flex-col items-center justify-center h-64 text-red-500">
              <AlertCircle className="w-12 h-12 mb-2" />
              <p>{error}</p>
            </div>
          )}
          
          {!loading && !error && blobUrl && isPdf && (
            <iframe
              src={blobUrl}
              className="w-full h-[65vh] rounded-lg border"
              title={contract.original_filename}
            />
          )}
          
          {!loading && !error && blobUrl && !isPdf && (
            <div className="flex flex-col items-center justify-center h-64 text-slate-500">
              <FileText className="w-12 h-12 mb-2" />
              <p>Aperçu non disponible pour ce format</p>
              <Button onClick={handleDownload} className="mt-4">
                <Download className="w-4 h-4 mr-2" />
                Télécharger le fichier
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Empty state
const EmptyState = ({ onUpload }) => (
  <div className="flex flex-col items-center justify-center py-12 text-center">
    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <FileSignature className="w-8 h-8 text-slate-400" />
    </div>
    <h3 className="text-lg font-semibold text-slate-900 mb-2">Aucun contrat</h3>
    <p className="text-sm text-slate-500 mb-4 max-w-sm">
      Ajoutez les contrats de travail, avenants et autres documents contractuels pour ce salarié.
    </p>
    <Button onClick={onUpload} className="bg-blue-600 hover:bg-blue-700">
      <Plus className="w-4 h-4 mr-2" />
      Ajouter le premier contrat
    </Button>
  </div>
);

// Upload form initial state
const INITIAL_FORM = {
  type: "",
  subject: "",
  signature_date: "",
  effective_date: "",
  end_date: "",
  amendment_number: "",
  parent_contract_id: "",
};

export default function ContractsTab({ employee, onUpdate }) {
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  
  // State
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Upload state
  const [isUploadMode, setIsUploadMode] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState(INITIAL_FORM);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Viewer state
  const [viewerContract, setViewerContract] = useState(null);
  
  // Delete state
  const [deleteContract, setDeleteContract] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Amendment mode
  const [parentContract, setParentContract] = useState(null);
  
  // Drag & drop state
  const [isDragging, setIsDragging] = useState(false);
  
  // Fetch contracts
  const fetchContracts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/hr/employees/${employee.id}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContracts(response.data.contracts || []);
    } catch (err) {
      console.error("Error fetching contracts:", err);
      setError("Impossible de charger les contrats");
    } finally {
      setLoading(false);
    }
  }, [employee.id, token]);
  
  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);
  
  // Filter visible contracts (not deleted, not amendments at top level)
  const visibleContracts = useMemo(() => {
    return contracts
      .filter(c => !c.is_deleted && c.type !== "amendment")
      .sort((a, b) => new Date(b.effective_date) - new Date(a.effective_date));
  }, [contracts]);
  
  // Get main contracts for amendment selection
  const mainContracts = useMemo(() => {
    return contracts.filter(c => 
      !c.is_deleted && 
      (c.type === "initial_contract" || c.type === "job_offer")
    );
  }, [contracts]);
  
  // Count total visible (including amendments)
  const visibleCount = useMemo(() => {
    return contracts.filter(c => !c.is_deleted).length;
  }, [contracts]);
  
  // Handle file selection
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error("Fichier invalide", { description: validation.error });
      return;
    }
    
    setSelectedFile(file);
    setIsUploadMode(true);
    
    // Pre-fill amendment info if adding an amendment
    if (parentContract) {
      const existingAmendments = contracts.filter(c => 
        c.parent_contract_id === parentContract.id && !c.is_deleted
      ).length;
      
      setUploadForm({
        ...INITIAL_FORM,
        type: "amendment",
        parent_contract_id: parentContract.id,
        amendment_number: String(existingAmendments + 1),
        subject: `Avenant n°${existingAmendments + 1} au ${parentContract.subject}`,
      });
    }
  };

  // Drag & drop handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    
    const validation = validateFile(file);
    if (!validation.valid) {
      toast.error("Fichier invalide", { description: validation.error });
      return;
    }
    
    setSelectedFile(file);
    setIsUploadMode(true);
  };
  
  // Update form field
  const updateForm = (field, value) => {
    setUploadForm(prev => ({ ...prev, [field]: value }));
  };
  
  // Cancel upload
  const cancelUpload = () => {
    setSelectedFile(null);
    setUploadForm(INITIAL_FORM);
    setIsUploadMode(false);
    setUploadProgress(0);
    setParentContract(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  
  // Validate form
  const validateForm = () => {
    if (!uploadForm.type) {
      toast.error("Type de contrat requis");
      return false;
    }
    if (!uploadForm.subject?.trim()) {
      toast.error("Objet du contrat requis");
      return false;
    }
    if (!uploadForm.signature_date) {
      toast.error("Date de signature requise");
      return false;
    }
    if (!uploadForm.effective_date) {
      toast.error("Date d'effet requise");
      return false;
    }
    if (uploadForm.type === "amendment" && !uploadForm.amendment_number) {
      toast.error("Numéro d'avenant requis");
      return false;
    }
    return true;
  };
  
  // Upload contract
  const uploadContract = async () => {
    if (!validateForm() || !selectedFile) return;
    
    setIsUploading(true);
    setUploadProgress(0);
    
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("type", uploadForm.type);
      formData.append("subject", uploadForm.subject);
      formData.append("signature_date", uploadForm.signature_date);
      formData.append("effective_date", uploadForm.effective_date);
      if (uploadForm.end_date) formData.append("end_date", uploadForm.end_date);
      if (uploadForm.type === "amendment") {
        formData.append("amendment_number", uploadForm.amendment_number);
        if (uploadForm.parent_contract_id) {
          formData.append("parent_contract_id", uploadForm.parent_contract_id);
        }
      }
      
      await axios.post(
        `${API}/hr/employees/${employee.id}/contracts`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(percent);
          }
        }
      );
      
      toast.success("Contrat ajouté avec succès !", {
        description: uploadForm.subject,
        duration: 4000
      });
      
      await fetchContracts();
      onUpdate?.();
      cancelUpload();
      
    } catch (err) {
      console.error("Error uploading contract:", err);
      toast.error("Erreur", { 
        description: err.response?.data?.detail || "Impossible d'ajouter le contrat" 
      });
    } finally {
      setIsUploading(false);
    }
  };
  
  // View contract
  const handleView = (contract) => {
    setViewerContract(contract);
  };
  
  // Download contract
  const handleDownload = async (contract) => {
    try {
      const response = await axios.get(
        `${API}/hr/files/${contract.file_id}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = contract.original_filename;
      link.click();
      URL.revokeObjectURL(url);
      
    } catch (err) {
      console.error("Error downloading contract:", err);
      toast.error("Erreur", { description: "Impossible de télécharger le contrat" });
    }
  };
  
  // Delete click
  const handleDeleteClick = (contract) => {
    setDeleteContract(contract);
  };
  
  // Confirm delete
  const confirmDelete = async () => {
    if (!deleteContract) return;
    
    setIsDeleting(true);
    try {
      await axios.delete(`${API}/hr/contracts/${deleteContract.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Contrat supprimé", {
        description: "Le contrat a été déplacé dans la corbeille",
        action: {
          label: "Annuler",
          onClick: () => handleRestore(deleteContract)
        },
        duration: 8000
      });
      
      setDeleteContract(null);
      await fetchContracts();
      onUpdate?.();
      
    } catch (err) {
      console.error("Error deleting contract:", err);
      toast.error("Erreur", { description: "Impossible de supprimer le contrat" });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Restore contract
  const handleRestore = async (contract) => {
    try {
      await axios.post(`${API}/hr/contracts/${contract.id}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Contrat restauré");
      await fetchContracts();
      onUpdate?.();
      
    } catch (err) {
      console.error("Error restoring contract:", err);
      toast.error("Erreur", { description: "Impossible de restaurer le contrat" });
    }
  };
  
  // Add amendment
  const handleAddAmendment = (contract) => {
    setParentContract(contract);
    fileInputRef.current?.click();
  };
  
  // Render loading
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }
  
  // Render error
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-red-500">
        <AlertCircle className="w-12 h-12 mb-2" />
        <p>{error}</p>
        <Button variant="outline" onClick={fetchContracts} className="mt-4">
          Réessayer
        </Button>
      </div>
    );
  }

  return (
    <div 
      className="space-y-6 relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag overlay */}
      {isDragging && (
        <div className="absolute inset-0 z-50 bg-blue-500/10 border-2 border-dashed border-blue-500 rounded-lg flex items-center justify-center backdrop-blur-sm">
          <div className="bg-white p-6 rounded-xl shadow-lg text-center">
            <Upload className="w-12 h-12 text-blue-500 mx-auto mb-3" />
            <p className="text-lg font-semibold text-slate-800">Déposez votre contrat ici</p>
            <p className="text-sm text-slate-500 mt-1">PDF, DOC ou DOCX acceptés</p>
          </div>
        </div>
      )}
      
      {/* Upload zone */}
      <Card className="border-2 border-dashed border-slate-200 hover:border-slate-300 transition-colors">
        <CardContent className="p-6">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
              <Upload className="w-6 h-6 text-slate-400" />
            </div>
            <p className="text-sm font-medium text-slate-700 mb-1">
              Ajouter un contrat
            </p>
            <p className="text-xs text-slate-500 mb-3">
              Glissez-déposez ou cliquez • PDF, DOC, DOCX • Max 10 Mo
            </p>
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf,.pdf,application/msword,.doc,application/vnd.openxmlformats-officedocument.wordprocessingml.document,.docx"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button 
              variant="outline" 
              onClick={() => {
                setParentContract(null);
                fileInputRef.current?.click();
              }}
              data-testid="upload-contract-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Sélectionner un fichier
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* Upload form */}
      {isUploadMode && selectedFile && (
        <Card className="border-blue-200 bg-blue-50/30">
          <CardHeader className="pb-4">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-blue-600" />
              Nouveau contrat
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* File info */}
            <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
              <FileText className="w-8 h-8 text-red-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{selectedFile.name}</p>
                <p className="text-xs text-slate-500">{formatFileSize(selectedFile.size)}</p>
              </div>
              {!isUploading && (
                <Button variant="ghost" size="icon" onClick={cancelUpload}>
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            {/* Progress */}
            {isUploading && (
              <div className="space-y-1">
                <Progress value={uploadProgress} className="h-2" />
                <p className="text-xs text-slate-500">{uploadProgress}%</p>
              </div>
            )}
            
            {/* Form fields */}
            {!isUploading && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Type */}
                <div className="space-y-1.5">
                  <Label className="text-sm">Type de contrat *</Label>
                  <Select value={uploadForm.type} onValueChange={(v) => updateForm("type", v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {CONTRACT_DOC_TYPE_OPTIONS.map(opt => (
                        <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Subject */}
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-sm">Objet / Intitulé *</Label>
                  <Input
                    value={uploadForm.subject}
                    onChange={(e) => updateForm("subject", e.target.value)}
                    placeholder="Ex: Contrat de travail CDI"
                  />
                </div>
                
                {/* Signature date */}
                <div className="space-y-1.5">
                  <Label className="text-sm">Date de signature *</Label>
                  <DatePickerFr
                    value={uploadForm.signature_date}
                    onChange={(v) => updateForm("signature_date", v)}
                    placeholder="JJ/MM/AAAA"
                  />
                </div>
                
                {/* Effective date */}
                <div className="space-y-1.5">
                  <Label className="text-sm">Date d'effet *</Label>
                  <DatePickerFr
                    value={uploadForm.effective_date}
                    onChange={(v) => updateForm("effective_date", v)}
                    placeholder="JJ/MM/AAAA"
                  />
                </div>
                
                {/* End date (optional) */}
                <div className="space-y-1.5">
                  <Label className="text-sm">Date de fin (si applicable)</Label>
                  <DatePickerFr
                    value={uploadForm.end_date}
                    onChange={(v) => updateForm("end_date", v)}
                    placeholder="JJ/MM/AAAA"
                  />
                </div>
                
                {/* Amendment fields */}
                {uploadForm.type === "amendment" && (
                  <>
                    <div className="space-y-1.5">
                      <Label className="text-sm">Numéro d'avenant *</Label>
                      <Input
                        type="number"
                        min="1"
                        value={uploadForm.amendment_number}
                        onChange={(e) => updateForm("amendment_number", e.target.value)}
                        placeholder="1"
                      />
                    </div>
                    
                    <div className="space-y-1.5">
                      <Label className="text-sm">Contrat parent</Label>
                      <Select 
                        value={uploadForm.parent_contract_id} 
                        onValueChange={(v) => updateForm("parent_contract_id", v)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner (optionnel)..." />
                        </SelectTrigger>
                        <SelectContent>
                          {mainContracts.map(c => (
                            <SelectItem key={c.id} value={c.id}>
                              {c.subject} ({formatDateFr(c.effective_date)})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
              </div>
            )}
            
            {/* Actions */}
            {!isUploading && (
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={cancelUpload}>
                  Annuler
                </Button>
                <Button onClick={uploadContract} className="bg-blue-600 hover:bg-blue-700">
                  <Upload className="w-4 h-4 mr-2" />
                  Enregistrer
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Contracts list */}
      {!isUploadMode && (
        <>
          {visibleContracts.length === 0 ? (
            <EmptyState onUpload={() => fileInputRef.current?.click()} />
          ) : (
            <Card className="border-slate-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-semibold text-slate-900 flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-blue-600" />
                  Contrats ({visibleCount})
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <ScrollArea className="max-h-[500px]">
                  <div className="space-y-3">
                    {visibleContracts.map(contract => (
                      <ContractItem
                        key={contract.id}
                        contract={contract}
                        allContracts={contracts}
                        onView={handleView}
                        onDownload={handleDownload}
                        onDelete={handleDeleteClick}
                        onAddAmendment={handleAddAmendment}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </>
      )}
      
      {/* Contract viewer */}
      <ContractViewer
        contract={viewerContract}
        open={!!viewerContract}
        onClose={() => setViewerContract(null)}
        token={token}
      />
      
      {/* Delete confirmation */}
      <AlertDialog open={!!deleteContract} onOpenChange={() => setDeleteContract(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce contrat ?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Le contrat <strong>"{deleteContract?.subject}"</strong> sera déplacé 
                dans la corbeille.
              </p>
              <p className="text-amber-600 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Il sera définitivement supprimé dans 30 jours.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDelete}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Suppression...
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  Supprimer
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
